import { ListData, SerializedListDataObject } from 'shared-components/interfaces';

/**
 * Countries whose addresses use the standard UK postcode format. Isle of Man (IM) and
 * the Channel Islands (GY, JE) share the format, so they are enforced alongside the four
 * nations.
 *
 * 'United Kingdom' is soft-deleted from the countryOfBirth list (migration 0128) but is
 * still served and still stored against older patient records, so it stays in the list —
 * dropping it would take validation away from addresses that currently have it.
 */
export const UK_POSTCODE_COUNTRY_NAMES = [
  'United Kingdom',
  'England',
  'Scotland',
  'Wales',
  'Northern Ireland',
  'Isle of Man',
  'Channel Islands',
];

/**
 * Whether an already-resolved country name is one we enforce UK postcode rules for.
 * Use isUkPostcodeCountry when holding a stored value that may still be a ListData id.
 */
export const isUkPostcodeCountryName = (countryName: string): boolean =>
  UK_POSTCODE_COUNTRY_NAMES.includes(countryName);

/**
 * Whether a stored country value is one of the countries we enforce UK postcode rules for.
 *
 * The country is normally a ListData id and has to be resolved to a name first. Countries
 * with no list entry are stored as the raw name instead — 'Channel Islands' is the one that
 * matters here, since the postcode lookup returns it but countryOfBirth has no entry for it
 * — so an unresolved value is compared as a name rather than treated as no match.
 */
export const isUkPostcodeCountry = (countryId: string, countries: ListData[]): boolean => {
  const country = countries.find((value): boolean => value.id === countryId);

  return isUkPostcodeCountryName(country ? country.name : countryId);
};

export const convertListDataToObject = (listData?: ListData[], key = 'id'): SerializedListDataObject | {} => {
  const initialValue: ListData | {} = {};

  if (!listData) return {};

  return listData.reduce((obj, item) => {
    return item[key as keyof typeof item]
      ? {
          ...obj,
          [item[key as keyof typeof item] as string]: item.name,
        }
      : obj;
  }, initialValue);
};
